import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { type PropsWithChildren } from 'react';
import {
	IntlProvider as IntlProviderNext,
	type MessageFormatElement,
	useIntl,
} from 'react-intl-next';

import { type MessageLoader, useIntlMessages } from './useIntlMessages';
import { type JSX } from 'react/jsx-runtime';

export type IntlProviderProps<T> = {
	messages?: T;
	messagesLoader: MessageLoader<T>;
};

function useFetchLocale() {
	let locale = 'en';
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	try {
		const { locale: intlLocale } = useIntl();
		locale = intlLocale;
	} catch (err) {
		fireAnalyticsEvent({
			action: 'errored',
			actionSubject: 'intlProvider',
			eventType: 'operational',
			attributes: {
				errorMessage: err,
			},
		});
	}

	return { locale };
}

export function IntlProvider<
	T extends Record<string, string> | Record<string, MessageFormatElement[]>,
>(props: PropsWithChildren<IntlProviderProps<T>>): JSX.Element {
	const { locale } = useFetchLocale();
	const messages = useIntlMessages(locale, props.messagesLoader, props.messages);

	return (
		<IntlProviderNext locale={locale} messages={messages}>
			{props.children}
		</IntlProviderNext>
	);
}
